import store from '@store';
import { logout, updateToken } from '@store/auth/auth.actions';
import { appInfoHeaders, refreshJwtToken } from '@store/auth/auth.service';
import { WFAxios } from './api.instance';

let refreshRequestPromise = null;

const waitForRetryDelay = (timeout) => new Promise((resolve) => setTimeout(resolve, timeout));

const performTokenRenewal = async () => {
  const MAX_RETRIES = 7;

  for (let retryCount = 0; retryCount < MAX_RETRIES; retryCount += 1) {
    try {
      const {
        auth: { tenantToken, refreshToken },
      } = store.getState();

      if (!refreshToken || !tenantToken) {
        throw new Error('Missing refresh session');
      }

      const { data } = await refreshJwtToken({ refreshToken, tenantToken });
      return data;
    } catch (error) {
      if (retryCount === MAX_RETRIES - 1) {
        throw error;
      }

      console.error(`Retry ${retryCount + 1} failed. Retrying...`);
      await waitForRetryDelay(5000);
    }
  }

  throw new Error('Giving up Retry!');
};

const renewToken = async () => {
  if (!refreshRequestPromise) {
    refreshRequestPromise = performTokenRenewal().finally(() => {
      refreshRequestPromise = null;
    });
  }

  return refreshRequestPromise;
};

export const requestSuccess = (config) => {
  const {
    auth: { authToken, tenantToken },
  } = store.getState();

  config.headers = {
    ...config.headers,
    ...appInfoHeaders,
  };

  if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
  if (tenantToken) {
    config.headers['X-TenantId'] = tenantToken;
  }

  if (config.headers.Authorization && authToken) return config;
  // eslint-disable-next-line no-throw-literal
  throw { message: 'Invalid token' };
};

export const requestFailure = async (error) => {
  const originalRequest = error.config;

  if (
    originalRequest &&
    (error.response?.status === 401 || error.response?.status === 403) &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true;

    try {
      // Renew token
      const data = await renewToken();
      store.dispatch(
        updateToken({
          authToken: data.jwt_token,
          refreshToken: data?.refresh_token,
        })
      );
      // Update the original request with the new token
      originalRequest.headers['Authorization'] = `Bearer ${data.jwt_token}`;

      // Retry the original request
      return WFAxios(originalRequest);
    } catch (renewError) {
      // Handle token renewal failure
      console.error('Token renewal failed:', renewError);

      // Redirect to login or handle the error accordingly
      // For example:
      // history.push('/login');
      localStorage.setItem('logout_msg', 'Token Renewal Failed.');
      store.dispatch(logout({ timedOut: false }));

      return Promise.reject(renewError);
    }
  } else {
    throw error;
  }
};
